import React, { useReducer, useEffect } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { usePaginatedQuery, CenteredLoader, InfiniteScrollComponent } from '@playbookux/playbook-web-core';

import { HIGHLIGHT_REELS } from '../graphql';
import { NoImageHighlightReelZeroState } from '../../../zero-state';
import NoResultMessage from '../../../no-results';
import useDebounce from '../../../../../core/hooks/use-debounce';
import useDispatchToActions from '../../../../../core/utils/dispatch';

import HighlighReelCard from './highligh-reel-card';
import {
  ContainerList,
  ContainerButton,
  AddButton,
  TextTotal,
  ClearButton,
  ContainerInputSearch,
  InputSearch,
} from './styles';

import reducer, {
  initialState,
} from '../reducer';

import {
  setSelectedCard,
  setClearSelection,
  setHasMore,
  setSearch,
} from '../actions';

const LIMIT = 20;
const DEBOUNCE_TIME = 700;

const ListHighlightReels = (props) => {
  const { companyId, onSetHighlightReels } = props;

  const [state, dispatch] = useReducer(reducer, initialState);

  const dispatchToActions = useDispatchToActions({
    setSelectedCard,
    setClearSelection,
    setHasMore,
    setSearch,
  }, dispatch);

  const {
    selectedCards,
    search,
  } = state;

  const [debouncedSearch = ''] = useDebounce(() => search, DEBOUNCE_TIME, [search]);

  const {
    setSelectedCard: onSetSelectedCard,
    setClearSelection: onSetClearSelection,
    setSearch: onSetSearch,
  } = dispatchToActions;

  const {
    loading,
    data = {},
    fetchMore,
    hasMore,
    refetch,
  } = usePaginatedQuery(HIGHLIGHT_REELS, {
    variables: {
      companyId,
      limit: LIMIT,
      offset: 0,
      order: 'created_at DESC',
      search: debouncedSearch,
    },
  }, {
    dataField: 'highlightReels',
  });

  const { highlightReels } = data;

  useEffect(() => {
    if (!loading) {
      refetch({
        variables: {
          search: debouncedSearch,
        },
      });
    }
  }, [loading, refetch, debouncedSearch]);

  const handleAddHighlightReels = () => {
    onSetHighlightReels(selectedCards);
  };

  const handleSearchChange = (e, { value }) => onSetSearch(value);

  const zeroState = highlightReels && _.isEmpty(highlightReels);
  const noResults = zeroState && !_.isEmpty(search);

  return (
    <>
      <ContainerList>

        <ContainerInputSearch>
          <InputSearch
            icon="search"
            placeholder="Search highlight reels"
            input={{
              icon: 'search',
              iconPosition: 'left',
            }}
            onSearchChange={handleSearchChange}
            showNoResults={false}
            value={search}
          />
        </ContainerInputSearch>

        {loading && (
          <CenteredLoader />
        )}

        {zeroState && !noResults && (
          <NoImageHighlightReelZeroState />
        )}

        {noResults && (
          <NoResultMessage />
        )}

        {highlightReels && !_.isEmpty(highlightReels) && (
          <InfiniteScrollComponent
            hasMore={hasMore}
            onLoadMore={fetchMore}
          >

            {highlightReels && highlightReels.map((highlightReel) => (
              <HighlighReelCard
                selected={!!selectedCards[highlightReel.id]}
                key={highlightReel.id}
                highlightReel={highlightReel}
                onCardClick={() => onSetSelectedCard(highlightReel)}
                isSelectable
              />
            ))}

          </InfiniteScrollComponent>
        )}

      </ContainerList>

      <ContainerButton>
        <TextTotal>
          {`${_.keys(selectedCards).length} Selected `}
          <ClearButton onClick={() => onSetClearSelection()}>
            Clear
          </ClearButton>
        </TextTotal>
        <AddButton
          onClick={() => handleAddHighlightReels()}
          disabled={_.isEmpty(selectedCards)}
        >
          Add
        </AddButton>
      </ContainerButton>
    </>
  );
};

ListHighlightReels.propTypes = {
  companyId: PropTypes.string.isRequired,
  onSetHighlightReels: PropTypes.func.isRequired,
};

export default ListHighlightReels;
